import { CheckCircle2, Info, Loader2, XCircle } from 'lucide-react';
import * as React from 'react';

import { IdentityApiError, type IdentityErrorCode } from '@/api/identity-client';
import { LegalApiError } from '@/api/legal-client';
import { useConsumeMagicLink } from '@/api/use-identity';
import { useHasSignedCgu } from '@/api/use-legal';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

// =============================================================================
// magic-link-screen.tsx — Écran F-01 « Consommation du magic-link ».
// =============================================================================
// UC-F-001 : à l'arrivée sur la route avec un token, l'UI ORCHESTRE la connexion
// en deux temps (Option B via A, GATE_HUMAN) :
//   1. identity.consumeMagicLink(token) — consomme le lien (usage unique strict,
//      IT-IDENTITY-02), l'API ouvre la SupplierSession opaque et résout le tenant
//      (siret via whoami). L'identity-client parle UNIQUEMENT identity.
//   2. legal.hasSigned() — statut CGU (BC legal, PAS identity), sondé SEULEMENT
//      après ouverture de session (gate `enabled`, sinon 401). Route la suite :
//      `hasSigned === false` → onboarding CGU (F-02), `true` → grille (EF-IDA-4).
// En échec (lien expiré/consommé/invalide, ou legal indisponible), message +
// action de remédiation adaptée au code.
//
// Composant PRÉSENTATIONNEL : il reçoit le `token` et un `onActivated` en props
// (testable hors routeur, comme ParametersScreen). L'adaptateur de route
// (magic-link-route) lit le token d'URL et déclenche la navigation.
//
// a11y (WCAG 2.1 AA) : <h1> unique, statut de chargement en role="status",
// erreur en role="alert", icônes décoratives aria-hidden (jamais l'icône seule).

/** Issue de l'orchestration de connexion : identité résolue + routing CGU. */
export interface MagicLinkOutcome {
  /** Tenant (SIRET) résolu par l'API (whoami) — l'UI se câble sur ce {siret}. */
  readonly tenantId: string;
  /** CGU courantes signées ? (legal.hasSigned) — pilote la redirection (EF-IDA-4). */
  readonly hasSigned: boolean;
}

export interface MagicLinkScreenProps {
  /** Token extrait de l'URL (param de route). */
  readonly token: string;
  /**
   * Appelé une fois la connexion établie (session ouverte + statut CGU connu) :
   * l'appelant route vers la suite (F-02 CGU si `!hasSigned`, grille sinon).
   */
  readonly onActivated: (outcome: MagicLinkOutcome) => void;
}

/** Libellé d'action de remédiation selon le code d'erreur (UC-F-001 E1/E2). */
function remediationLabel(code: IdentityErrorCode): string {
  switch (code) {
    case 'TOKEN_CONSUMED':
      return 'Se reconnecter';
    case 'TOKEN_EXPIRED':
    case 'TOKEN_INVALID':
    case 'RATE_LIMITED':
    case 'UNKNOWN':
      return 'Demander un nouveau lien';
  }
}

export function MagicLinkScreen({
  token,
  onActivated,
}: MagicLinkScreenProps): React.JSX.Element {
  const consume = useConsumeMagicLink();
  // Statut CGU sondé UNIQUEMENT après ouverture de session (consume réussi) : gate
  // `enabled` — sinon whoami/has-signed partiraient sans session (401). Étape 2.
  const cgu = useHasSignedCgu({ enabled: consume.isSuccess });

  // Consommation une seule fois à l'arrivée (usage unique strict). Le ref évite
  // une double consommation sous StrictMode / re-render (le 2e appel échouerait
  // TOKEN_CONSUMED côté serveur ; on ne le déclenche tout simplement pas).
  const triggered = React.useRef(false);
  React.useEffect(() => {
    if (triggered.current || token.length === 0) {
      return;
    }
    triggered.current = true;
    consume.mutate(token);
    // consume stable pour la durée de vie de l'écran ; déclenché une seule fois
    // (garde `triggered`). Dépendance volontairement sur `token`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Notification UNE seule fois quand les deux étapes sont résolues (session +
  // statut CGU) : la couche présentation route alors sur `hasSigned` (EF-IDA-4).
  const notified = React.useRef(false);
  React.useEffect(() => {
    if (
      notified.current ||
      !consume.isSuccess ||
      consume.data === undefined ||
      !cgu.isSuccess ||
      cgu.data === undefined
    ) {
      return;
    }
    notified.current = true;
    onActivated({
      tenantId: consume.data.tenantId,
      hasSigned: cgu.data.hasSigned,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consume.isSuccess, consume.data, cgu.isSuccess, cgu.data]);

  // DÉGRADÉ GRACIEUX (bug prod onboarding) : session ouverte (consume OK) MAIS aucune CGU
  // publiée côté admin → has-signed renvoie 404 (NoPublishedCguError). Ce n'est PAS une panne
  // ni une faute du fournisseur : la connexion a réussi, il n'y a simplement rien à signer
  // tant que l'admin n'a pas publié de texte. On l'affiche en MESSAGE INFORMATIF (role=status),
  // sans crash technique, SANS router vers la grille ni vers la signature (rien à signer).
  const noCguPublished =
    consume.isSuccess &&
    cgu.error instanceof LegalApiError &&
    cgu.error.isNoPublishedCgu;

  // Erreur = échec de l'une des deux étapes, HORMIS le 404 « aucune CGU publiée » traité à part.
  // Le code fin ne vient que d'identity (IdentityApiError) ; un autre échec legal retombe sur UNKNOWN.
  const activeError = consume.error ?? cgu.error;
  const errorCode =
    consume.error instanceof IdentityApiError ? consume.error.code : 'UNKNOWN';
  const isError = consume.isError || (cgu.isError && !noCguPublished);
  const isSuccess = consume.isSuccess && cgu.isSuccess;
  // Chargement tant qu'aucune issue n'est atteinte (succès, erreur, ou dégradé « pas de CGU »).
  const isPending = !isError && !isSuccess && !noCguPublished;

  return (
    <div className="mx-auto max-w-md space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Connexion en cours
        </h1>
      </header>

      <Card aria-labelledby="magic-link-title">
        <CardHeader>
          <CardTitle id="magic-link-title" className="text-lg">
            Vérification de votre lien
          </CardTitle>
          <CardDescription>
            Nous vérifions votre lien de connexion à usage unique.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isPending ? (
            <div
              role="status"
              className="flex items-center gap-3 text-muted-foreground"
            >
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              <span>Connexion en cours…</span>
            </div>
          ) : null}

          {isSuccess ? (
            <div
              role="status"
              className="flex items-start gap-3 rounded-md border border-success/40 bg-success/10 p-4"
            >
              <CheckCircle2
                className="mt-0.5 h-5 w-5 shrink-0 text-success"
                aria-hidden="true"
              />
              <p className="text-sm font-medium text-success">
                Connexion réussie. Redirection en cours…
              </p>
            </div>
          ) : null}

          {noCguPublished ? (
            // Dégradé gracieux : connexion réussie mais aucune CGU publiée (404). Message
            // informatif (role=status), couleur DOUBLÉE d'un libellé explicite, pas de crash,
            // pas de redirection. L'icône est décorative (aria-hidden) — le texte porte le sens.
            <div
              role="status"
              className="flex items-start gap-3 rounded-md border border-border bg-muted p-4"
            >
              <Info
                className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <div className="space-y-1">
                <p className="font-medium text-foreground">
                  Votre connexion a réussi
                </p>
                <p className="text-sm text-foreground">
                  Les conditions générales ne sont pas encore disponibles ;
                  contactez l&apos;administrateur JeKo pour finaliser votre accès.
                </p>
              </div>
            </div>
          ) : null}

          {isError ? (
            <div className="space-y-4">
              <div
                role="alert"
                className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-4"
              >
                <XCircle
                  className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
                  aria-hidden="true"
                />
                <div className="space-y-1">
                  <p className="font-medium text-destructive">
                    Lien de connexion non valide
                  </p>
                  <p className="text-sm text-foreground">
                    {activeError?.message}
                  </p>
                </div>
              </div>
              <Button type="button" variant="outline" asChild>
                <a href="/">{remediationLabel(errorCode)}</a>
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
